import React, { useEffect, useRef, useState } from 'react';
import { ST, PAYMENT_LOGOS, countryName, flagEmoji, fmtPrice } from './Chrome';

///////////////////////////////////////////////////////////////////////////////
// eSIM plan detail modal + the small helpers it needs — shared between the
// eSIM home (/dataplans) and the country landing pages (/esim/<slug>).
// Moved out of App.jsx unchanged so both can import it without cycles.
///////////////////////////////////////////////////////////////////////////////

export function durationLabel(p) {
  if (!p.duration) return '';
  const unit = (p.duration_unit || 'day').toLowerCase();
  return `${p.duration} ${unit}${p.duration > 1 ? 's' : ''}`;
}

// eSIM Access writes "UK" for Britain; flags need ISO-3166 codes.
const EXIT_ISO = { UK: 'GB' };

// "UK/NO" -> ['GB', 'NO']
export function exitCountries(v) {
  return String(v || '').split('/').map((s) => EXIT_ISO[s.trim()] || s.trim()).filter(Boolean);
}

export function exitNames(v) {
  return exitCountries(v).map((c) => (/^[A-Z]{2}$/.test(c) ? countryName(c) : c)).join(' or ');
}

export const DAY_PRESETS = [3, 5, 7, 10, 15, 30];

const ACTIVE_ON_FIRST_USE = '2';

export function PlanDetailModal({ detail, onClose, onBuy, initialDays }) {
  const { p, full, error } = detail;
  const closeRef = useRef(null);
  // Daily plans are priced per day, so the length is decided here rather than
  // at checkout. A deep link (/esim/<c>/daily_1_22) can preselect the days.
  const [days, setDays] = useState(initialDays || 3);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const d = full || p;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={p.name}>
        <div className="modal-head">
          <div>
            <div className="modal-title">{p.name}</div>
            <div className="modal-chips">
              {d.data_label && <span className="mchip">{d.data_label}</span>}
              {d.duration && <span className="mchip">{p.is_daily ? `${days} day${days !== 1 ? 's' : ''}` : durationLabel(d)}</span>}
              {full && full.speed && <span className="mchip">{full.speed}</span>}
              {full && (full.supports_topup === 1 || full.supports_topup === 2 || full.supports_topup === true) && <span className="mchip ok">Top-up later</span>}
              {full && full.supports_topup === 3 && <span className="mchip ok">Add more days</span>}
            </div>
          </div>
          <div className="modal-price">
            <b>{fmtPrice(d.price)}</b>
            {p.per_gb != null && <span>{`$${p.per_gb.toFixed(2)}/GB`}</span>}
          </div>
          <button type="button" className="modal-x" onClick={onClose} ref={closeRef} aria-label="Close">✕</button>
        </div>

        <div className="modal-body">
          {error && <div className="notice">Couldn’t load plan details — the basics above are still accurate.</div>}
          {!full && !error && <div className="modal-loading">Loading networks…</div>}

          {full && full.networks && full.networks.length > 0 && (
            <section>
              <h5>Coverage &amp; networks</h5>
              <div className="netlist">
                {full.networks.map((n) => (
                  <div className="netrow" key={n.code}>
                    <span className="flag">{/^[A-Z]{2}$/.test(n.code || '') ? flagEmoji(n.code) : '🌐'}</span>
                    <span className="nname">{n.name}</span>
                    <span className="nops">
                      {n.operators.map((o) => `${o.name}${o.type ? ` ${o.type}` : ''}`).join(' · ') || '—'}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {p.is_daily && (
            <section>
              <h5>How many days?</h5>
              <div className="daypick">
                {DAY_PRESETS.map((v) => (
                  <button type="button" key={v} className={`chip ${days === v ? 'active' : ''}`}
                    onClick={() => setDays(v)}>{v}</button>
                ))}
                <label className={`chip custom ${!DAY_PRESETS.includes(days) ? 'active' : ''}`}>
                  <input
                    type="number" min="1" max="90" value={days}
                    onChange={(e) => { const v = parseInt(e.target.value, 10); if (v >= 1 && v <= 90) setDays(v); }}
                    aria-label="Number of days"
                  /> days
                </label>
              </div>
            </section>
          )}

          <section>
            <h5>Good to know</h5>
            <ul className="facts">
              {full && (
                <li>
                  <b>Topup:</b>{' '}
                  {full.supports_topup === 3
                    ? 'You can add more days to this eSIM later — no new QR code needed.'
                    : full.supports_topup
                      ? 'You can top up this eSIM with more data later — no new QR code needed.'
                      : 'Fixed plan — when it runs out, you buy and install a new eSIM.'}
                </li>
              )}
              <li>
                <b>Activation:</b>{' '}
                {full && full.active_type === ACTIVE_ON_FIRST_USE
                  ? 'Validity starts when the eSIM first connects to a network at your destination — install it before you fly, it won’t start counting.'
                  : 'Validity starts once the eSIM is activated.'}
              </li>
              {full && full.install_window_days && (
                <li><b>Install window:</b> activate within {full.install_window_days} days of purchase.</li>
              )}
              {full && full.is_daily && (
                <li>
                  <b>Daily plan:</b> the data allowance resets every day
                  {full.fup_policy ? `; after it’s used, speed drops to ${full.fup_policy} until the next day` : ''}.
                </li>
              )}
              {full && full.data_exit && (
                <li>
                  <b>Data exit:</b> your traffic reaches the internet in {exitNames(full.data_exit)} —
                  websites and apps will see an IP located there.
                </li>
              )}
              <li><b>Your number:</b> data-only plan — your regular SIM and number keep working for calls, WhatsApp and SMS.</li>
              <li><b>Device:</b> needs an unlocked, eSIM-compatible phone (iPhone XS or newer, most recent Androids).</li>
              <li><b>Refunds:</b> automatic refund if no data has been used. If your eSIM is in use, you can request a manual refund.</li>
            </ul>
          </section>

          <div className="modal-pay">
            Pay with card, Thai Banks, Alipay, PayPal, or your balance.
            <div className="paylogos">
              {PAYMENT_LOGOS.map((l) => (
                <img key={l.file} src={`${ST}/images/payment-icons/${l.file}`} alt={l.name} title={l.name} />
              ))}
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="linkish" onClick={onClose}>Close</button>
          <button type="button" className="btn" style={{ padding: '9px 28px' }}
            onClick={() => onBuy(p, p.is_daily ? days : undefined)}>
            Buy · {fmtPrice(p.is_daily ? d.price * days : d.price)}
          </button>
        </div>
      </div>
    </div>
  );
}
